import { createContext, ReactNode, useCallback, use, useMemo } from "react";

interface TimingExclusionContextValue {
  timingExcludedPeriodIds: Set<number>;
  toggleTimingExclusion: (periodIds: number[]) => void;
  areAllTimingExcluded: (periodIds: number[]) => boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const NOOP_VALUE: TimingExclusionContextValue = {
  timingExcludedPeriodIds: new Set(),
  toggleTimingExclusion: noop,
  areAllTimingExcluded: () => false,
};

const TimingExclusionContext =
  createContext<TimingExclusionContextValue | null>(null);

export const useTimingExclusion = (): TimingExclusionContextValue => {
  const context = use(TimingExclusionContext);
  return context ?? NOOP_VALUE;
};

export const TimingExclusionProvider = ({
  timingExcludedPeriodIds,
  setTimingExcludedPeriodIds,
  children,
}: {
  timingExcludedPeriodIds: Set<number>;
  setTimingExcludedPeriodIds: React.Dispatch<React.SetStateAction<Set<number>>>;
  children: ReactNode;
}) => {
  const toggleTimingExclusion = useCallback(
    (periodIds: number[]) => {
      setTimingExcludedPeriodIds((prev) => {
        const next = new Set(prev);
        const allExcluded = periodIds.every((id) => next.has(id));
        if (allExcluded) {
          periodIds.forEach((id) => next.delete(id));
        } else {
          periodIds.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [setTimingExcludedPeriodIds]
  );

  const areAllTimingExcluded = useCallback(
    (periodIds: number[]) =>
      periodIds.length > 0 &&
      periodIds.every((id) => timingExcludedPeriodIds.has(id)),
    [timingExcludedPeriodIds]
  );

  const value = useMemo(
    () => ({
      timingExcludedPeriodIds,
      toggleTimingExclusion,
      areAllTimingExcluded,
    }),
    [timingExcludedPeriodIds, toggleTimingExclusion, areAllTimingExcluded]
  );

  return (
    <TimingExclusionContext value={value}>{children}</TimingExclusionContext>
  );
};
